import { useState } from 'react';
import { CheckCircle2, Circle, Loader2, Sparkles } from 'lucide-react';
import { useAimdoc, useObjectives } from '@aimdoc/sdk-react';
import type { AimdocObjective } from '@aimdoc/sdk';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAimdocServicesClient } from '../user/useAimdocServicesClient';
import useAnalytics from '../analytics/analytics';

/**
 * Circular progress ring with "done/total" inside — the card's only
 * indicator, so it doubles as the eye-catcher.
 */
function ProgressRing({ done, total, size }: { done: number; total: number; size: number }) {
  const stroke = Math.max(2, Math.round(size / 11));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = total > 0 ? Math.min(done / total, 1) : 0;
  const fontSize = Math.round(size * 0.33);
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      aria-label={`${done} of ${total} done`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-muted-foreground/25"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          className="text-primary transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums leading-none"
        style={{ fontSize }}
      >
        {done}/{total}
      </span>
    </span>
  );
}

/**
 * Left-nav "Get started" card: the signed-in user's customer-facing
 * objectives, read through the public Services SDK exactly the way a
 * customer's product would (useObjectives + aimdoc.askAboutObjective).
 * Hidden until there is something to do; disappears once everything is
 * done. Each open step hands the user to the in-app agent, which explains
 * what that step takes and records progress as it happens.
 */
export default function GettingStartedCard({ collapsed }: { collapsed: boolean }) {
  const aimdoc = useAimdoc();
  const client = useAimdocServicesClient();
  const { objectives, incomplete, completed, loading, error } = useObjectives(client);
  const { captureEvent } = useAnalytics();
  const [open, setOpen] = useState(false);

  if (!client || loading || error || objectives.length === 0 || incomplete.length === 0) {
    return null;
  }

  const total = objectives.length;
  const done = completed.length;

  const askAgent = (objective: AimdocObjective) => {
    captureEvent('getting_started_ask_agent', { objective_key: objective.key });
    setOpen(false);
    aimdoc.askAboutObjective(objective);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {collapsed ? (
          <button
            type="button"
            className="mx-auto flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent/50"
            title={`Get started: ${done} of ${total} done`}
          >
            <ProgressRing done={done} total={total} size={28} />
          </button>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-lg border border-border bg-card/60 px-2.5 py-2 text-left transition-colors hover:bg-accent/50"
          >
            <ProgressRing done={done} total={total} size={30} />
            <span className="truncate text-[13px] font-medium">Get started</span>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={12} className="w-80 p-0">
        <div className="flex items-center gap-2.5 border-b px-4 py-3">
          <ProgressRing done={done} total={total} size={30} />
          <span className="text-sm font-semibold">Get started</span>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1">
          {incomplete.map((objective) => (
            <li key={objective.key} className="group flex items-center gap-2.5 px-4 py-2">
              {objective.status === 'in_progress' ? (
                <Loader2 className="size-4 shrink-0 animate-spin text-primary" strokeWidth={1.75} />
              ) : (
                <Circle className="size-4 shrink-0 text-muted-foreground/60" strokeWidth={1.75} />
              )}
              <span className="min-w-0 flex-1 truncate text-[13px]">{objective.name}</span>
              <button
                type="button"
                onClick={() => askAgent(objective)}
                className="flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-primary opacity-80 transition-opacity hover:bg-primary/10 hover:opacity-100"
                title="Ask the agent how to complete this"
              >
                <Sparkles className="size-3.5" strokeWidth={1.75} />
                Ask
              </button>
            </li>
          ))}
          {completed.map((objective) => (
            <li key={objective.key} className="flex items-center gap-2.5 px-4 py-2 text-muted-foreground">
              <CheckCircle2 className="size-4 shrink-0 text-green-600 dark:text-green-400" strokeWidth={1.75} />
              <span className="min-w-0 flex-1 truncate text-[13px] line-through">{objective.name}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
